// VendTrade "My listings + matches" — the personal home for a trader.
// Top: horizontal cards of the caller's active/inactive listings with
// edit + archive controls. Below: match groups grouped by the caller's
// own listing, sorted by best score.
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, Archive, Undo2, Trash2, MapPin, Star, Image as ImageIcon } from "lucide-react";
import { CategoryChip, DirectionPill } from "./shared";
import ImageUploader from "@/components/ImageUploader";

export default function VendTradeMine() {
  const nav = useNavigate();
  const [mine, setMine] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [m, x] = await Promise.all([
        api.get("/vendtrade/listings/mine"),
        api.get("/vendtrade/matches"),
      ]);
      setMine(m.data.items || []);
      setMatches(x.data.items || []);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleActive = async (l) => {
    try {
      await api.patch(`/vendtrade/listings/${l.id}`, { active: !l.active });
      toast.success(l.active ? "Listing archived" : "Listing reactivated");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`Delete "${l.title}" for good?`)) return;
    try {
      await api.delete(`/vendtrade/listings/${l.id}`);
      toast.success("Listing deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const updatePhoto = async (l, url) => {
    try {
      await api.patch(`/vendtrade/listings/${l.id}`, { image_url: url });
      toast.success(url ? "Photo added" : "Photo removed");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="mx-auto max-w-5xl px-6 py-14" data-testid="vendtrade-mine-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="overline" style={{ color: "var(--terracotta)" }}>VendTrade · My listings</div>
          <h1 className="font-serif text-5xl mt-3">Your trades &amp; matches.</h1>
        </div>
        <Button
          onClick={() => nav("/vendtrade/new")}
          data-testid="vendtrade-mine-new"
          className="btn-primary py-5 px-7 rounded-full inline-flex items-center gap-2"
        >
          <Plus size={16} /> New listing
        </Button>
      </div>

      {error && (
        <div className="mt-6 rounded-md p-3 text-sm"
             style={{ background: "rgba(196,79,54,0.08)", color: "var(--terracotta)" }}>
          {error}
        </div>
      )}

      <section className="mt-10">
        <h2 className="text-lg font-medium mb-4">Your listings</h2>
        {loading ? (
          <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Loading…</div>
        ) : mine.length === 0 ? (
          <div
            data-testid="vendtrade-mine-empty"
            className="rounded-lg border p-8 text-center"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              You haven&rsquo;t listed anything yet. Post one and we&rsquo;ll
              start matching you with people.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="vendtrade-mine-list">
            {mine.map((l) => (
              <div
                key={l.id}
                data-testid={`vendtrade-mine-card-${l.id}`}
                className="rounded-lg border p-5"
                style={{
                  borderColor: "rgba(0,0,0,0.1)",
                  background: l.active ? "#fff" : "rgba(0,0,0,0.02)",
                  opacity: l.active ? 1 : 0.75,
                }}
              >
                <div className="flex gap-4">
                  {l.image_url ? (
                    <img
                      src={l.image_url}
                      alt=""
                      data-testid={`vendtrade-mine-thumb-${l.id}`}
                      className="w-20 h-20 rounded-md object-cover shrink-0 border"
                      style={{ borderColor: "rgba(0,0,0,0.08)" }}
                    />
                  ) : (
                    <div
                      className="w-20 h-20 rounded-md border-2 border-dashed flex items-center justify-center shrink-0"
                      style={{ borderColor: "rgba(0,0,0,0.12)", color: "var(--ink-soft)" }}
                    >
                      <ImageIcon size={20} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <DirectionPill value={l.direction} />
                      <CategoryChip value={l.category} />
                    </div>
                    <Link
                      to={`/vendtrade/listings/${l.id}`}
                      className="font-serif text-xl leading-tight hover:underline block truncate"
                    >
                      {l.title}
                    </Link>
                    <p className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                      <MapPin size={11} className="inline mr-1" />
                      {l.city} · {l.contact_requests || 0} contact{l.contact_requests === 1 ? "" : "s"}
                    </p>
                  </div>
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  <ImageUploader
                    onUploaded={(url) => updatePhoto(l, url)}
                    label={l.image_url ? "Replace photo" : "Add photo"}
                    testId={`vendtrade-mine-image-${l.id}`}
                  />
                  {l.image_url && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => updatePhoto(l, "")}
                      data-testid={`vendtrade-mine-image-clear-${l.id}`}
                      className="rounded-full text-xs"
                    >
                      Remove photo
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => toggleActive(l)}
                    data-testid={`vendtrade-mine-toggle-${l.id}`}
                    className="rounded-full inline-flex items-center gap-1"
                  >
                    {l.active ? <><Archive size={12} /> Archive</> : <><Undo2 size={12} /> Reactivate</>}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => remove(l)}
                    data-testid={`vendtrade-mine-delete-${l.id}`}
                    className="rounded-full inline-flex items-center gap-1"
                    style={{ color: "var(--terracotta)" }}
                  >
                    <Trash2 size={12} /> Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="mt-14">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles size={18} style={{ color: "var(--terracotta)" }} />
          <h2 className="text-lg font-medium">Matches for you</h2>
        </div>
        {loading ? (
          <div className="text-sm" style={{ color: "var(--ink-soft)" }}>Scoring matches…</div>
        ) : matches.length === 0 ? (
          <div
            data-testid="vendtrade-matches-empty"
            className="rounded-lg border p-8 text-center"
            style={{ borderColor: "rgba(0,0,0,0.1)", background: "rgba(0,0,0,0.02)" }}
          >
            <p className="text-sm" style={{ color: "var(--ink-soft)" }}>
              No matches yet. Add more detail (or more listings) and check back &mdash;
              we score by category, city, and keyword overlap.
            </p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="vendtrade-matches-list">
            {matches.map((group) => (
              <MatchGroup key={group.listing.id} group={group} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function MatchGroup({ group }) {
  return (
    <div
      data-testid={`vendtrade-match-group-${group.listing.id}`}
      className="rounded-lg border p-5"
      style={{ borderColor: "rgba(0,0,0,0.1)", background: "#fff" }}
    >
      <div className="flex flex-wrap items-baseline gap-2">
        <span className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>
          For your listing
        </span>
        <Link
          to={`/vendtrade/listings/${group.listing.id}`}
          className="font-serif text-lg hover:underline"
        >
          {group.listing.title}
        </Link>
        <DirectionPill value={group.listing.direction} className="ml-1" />
      </div>
      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
        {group.matches.map((m) => (
          <Link
            key={m.id}
            to={`/vendtrade/listings/${m.id}`}
            data-testid={`vendtrade-match-${m.id}`}
            className="block rounded-md border overflow-hidden hover:shadow-sm transition"
            style={{ borderColor: "rgba(0,0,0,0.08)", background: "rgba(255,251,244,0.6)" }}
          >
            {m.image_url && (
              <img
                src={m.image_url}
                alt=""
                loading="lazy"
                className="w-full aspect-[16/9] object-cover"
              />
            )}
            <div className="p-4">
            <div className="flex items-center justify-between gap-2 mb-2">
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest px-2 py-0.5 rounded-full"
                style={{ background: "rgba(196,79,54,0.12)", color: "var(--terracotta)" }}
                data-testid={`vendtrade-match-score-${m.id}`}
              >
                <Star size={10} /> {m.match_score} fit
              </span>
              <CategoryChip value={m.category} />
            </div>
            <div className="font-serif text-base leading-tight">{m.title}</div>
            <p className="mt-1 text-xs line-clamp-2" style={{ color: "var(--ink-soft)" }}>
              {m.description}
            </p>
            <div className="mt-3 pt-2 border-t text-xs flex items-center justify-between"
                 style={{ borderColor: "rgba(0,0,0,0.06)", color: "var(--ink-soft)" }}>
              <span className="inline-flex items-center gap-1">
                <MapPin size={11} /> {m.city}
              </span>
              <span className="truncate max-w-[140px]">
                {m.owner?.business_name || m.owner?.name}
              </span>
            </div>
            {m.match_reasons?.length > 0 && (
              <ul className="mt-2 text-[11px] space-y-0.5" style={{ color: "var(--ink-soft)" }}>
                {m.match_reasons.map((r, i) => (
                  <li key={i}>&middot; {r}</li>
                ))}
              </ul>
            )}
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
}
